/**
 * Request/response schemas for the gastroenterology specialty module (`BACKLOG.md` M5). Same
 * reasoning as `dermatologyRequests.ts`/`aestheticsRequests.ts`: these live in `shared/` so the server
 * validates against the exact schema the web app infers its types from, every object schema is strict
 * so an unrecognised key is a rejection rather than silently ignored, and no server-owned value (an
 * id, `orgId`, a timestamp, `performingProviderId`, a computed surveillance/billing recommendation)
 * appears in any request schema — the route layer supplies those from the session or derives them,
 * never taking them from the body.
 */

import { z } from 'zod';

import { calendarDateSchema, confidentialityReasonsInputSchema } from './clinicalRequests.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';
import { CONSENT_SIGNING_METHODS } from './consentDocuments.ts';
import { PAYER_CATEGORIES, PROCEDURE_INDICATIONS } from './gastroenterology/billingIntent.ts';
import { BOWEL_PREP_SEGMENT_SCORES } from './gastroenterology/bowelPrep.ts';
import {
  COLON_SEGMENTS,
  DYSPLASIA_GRADES,
  HISTOLOGY_TYPES,
  PARIS_CLASSIFICATIONS,
  REMOVAL_METHODS,
} from './gastroenterology/polypFindings.ts';
import { SURVEILLANCE_CATEGORIES } from './gastroenterology/surveillanceIntervals.ts';

export const MAX_CONSENT_VERSION_LENGTH = 100;

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) => trimmed(max).optional();

/** Colonoscopy, EGD, flexible sigmoidoscopy, or another endoscopic procedure — open-but-checked at
 * the application boundary like every other closed-but-textual field in this codebase's schemas
 * (`dermatology_patch_test_readings.reading`'s own convention), since a practice's own procedure
 * catalog can genuinely vary. */
export const PROCEDURE_TYPES = ['colonoscopy', 'egd', 'flexible_sigmoidoscopy', 'other'] as const;

const bowelPrepSegmentSchema = z.union([
  z.literal(BOWEL_PREP_SEGMENT_SCORES[0]),
  z.literal(BOWEL_PREP_SEGMENT_SCORES[1]),
  z.literal(BOWEL_PREP_SEGMENT_SCORES[2]),
  z.literal(BOWEL_PREP_SEGMENT_SCORES[3]),
]);

// --- Procedure consent ------------------------------------------------------------------------------

export const createProcedureConsentRequestSchema = z
  .object({
    documentVersion: optionalTrimmed(MAX_CONSENT_VERSION_LENGTH),
    signedOn: calendarDateSchema,
    method: z.enum(CONSENT_SIGNING_METHODS).default('in_person'),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateProcedureConsentRequest = z.infer<typeof createProcedureConsentRequestSchema>;

export const procedureConsentResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  // Response, not request — plain strings, matching `dermatologyRequests.ts`'s own split: the narrow
  // request-side enums guard what a caller may write; a value already stored is read back as-is.
  consentType: z.string(),
  documentVersion: z.string().nullable(),
  signedOn: calendarDateSchema,
  signedByUserId: z.uuid(),
  method: z.string(),
  // Always null in ordinary operation — no route in this module exposes revoke (this schema's own
  // module header). Present for structural parity with `aestheticsRequests.ts`'s
  // `consentDocumentResponseSchema.revokedAt`.
  revokedAt: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
});
export type ProcedureConsentResponse = z.infer<typeof procedureConsentResponseSchema>;

export const procedureConsentEnvelopeSchema = z.object({
  consent: procedureConsentResponseSchema,
});
export const procedureConsentListEnvelopeSchema = z.object({
  consents: z.array(procedureConsentResponseSchema),
});

// --- Polyps -------------------------------------------------------------------------------------
// Declared before endoscopy reports below, which embeds `polypResponseSchema` directly in its own
// response — plain declaration order rather than `z.lazy`, since this is a one-way embed, not a
// genuinely recursive/mutually-referential shape.

export const createPolypRequestSchema = z
  .object({
    location: z.enum(COLON_SEGMENTS),
    sizeMm: z.number().int().positive(),
    parisClassification: z.enum(PARIS_CLASSIFICATIONS),
    removalMethod: z.enum(REMOVAL_METHODS),
  })
  .strict();
export type CreatePolypRequest = z.infer<typeof createPolypRequestSchema>;

/** Recording pathology is a separate call from creating the polyp — the result is never known in the
 * room (`polypFindings.ts`'s `PATHOLOGY_STATUSES` header). Both fields are required together: a
 * pathology report that came back "no dysplasia identified" still needs `dysplasiaGrade` left
 * `null` explicitly (this schema's own optional-with-default shape), never silently unset by a
 * partial update to some other field. */
export const recordPolypPathologyRequestSchema = z
  .object({
    histology: z.enum(HISTOLOGY_TYPES),
    dysplasiaGrade: z.enum(DYSPLASIA_GRADES).nullable().default(null),
  })
  .strict();
export type RecordPolypPathologyRequest = z.infer<typeof recordPolypPathologyRequestSchema>;

export const polypResponseSchema = z.object({
  id: z.uuid(),
  reportId: z.uuid(),
  patientId: z.uuid(),
  location: z.string(),
  sizeMm: z.number().int(),
  parisClassification: z.string(),
  removalMethod: z.string(),
  pathologyStatus: z.string(),
  histology: z.string().nullable(),
  dysplasiaGrade: z.string().nullable(),
});
export type PolypResponse = z.infer<typeof polypResponseSchema>;

export const polypEnvelopeSchema = z.object({ polyp: polypResponseSchema });

// --- Endoscopy reports -------------------------------------------------------------------------------

export const bostonBowelPrepScoreRequestSchema = z.object({
  rightColon: bowelPrepSegmentSchema,
  transverseColon: bowelPrepSegmentSchema,
  leftColon: bowelPrepSegmentSchema,
});

export const createEndoscopyReportRequestSchema = z
  .object({
    procedureType: z.enum(PROCEDURE_TYPES),
    indication: z.enum(PROCEDURE_INDICATIONS),
    performedOn: calendarDateSchema,
    /** The `treatment_general` consent on file for this procedure — validated server-side
     * (`reportService.ts`'s `requireProcedureConsent`) to belong to this patient and not be revoked,
     * never merely trusted because an id was supplied. */
    consentDocumentId: z.uuid(),
    insertionTimeMinutes: z.number().positive().optional(),
    withdrawalTimeMinutes: z.number().positive().optional(),
    cecalIntubationReached: z.boolean().optional(),
    bowelPrepScore: bostonBowelPrepScoreRequestSchema.optional(),
    piecemealResectionOfLargePolyp: z.boolean().default(false),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateEndoscopyReportRequest = z.infer<typeof createEndoscopyReportRequestSchema>;

export const bostonBowelPrepScoreResponseSchema = z.object({
  rightColon: z.number().int(),
  transverseColon: z.number().int(),
  leftColon: z.number().int(),
});

/** The surveillance recommendation computed at read time from this report's polyps — never stored,
 * matching `dermatology/routes.ts`'s `destructionCptLineResponseSchema` reasoning for why a coding/
 * clinical-rule result is derived rather than persisted: a future correction to the UNREVIEWED
 * `surveillanceIntervals.ts` table must not leave a stale recommendation behind on an old report. */
export const surveillanceRecommendationResponseSchema = z.object({
  category: z.enum(SURVEILLANCE_CATEGORIES),
  minYears: z.number().positive(),
  maxYears: z.number().positive(),
  reason: z.string(),
  /** Always true — a loud, permanent reminder on every response carrying this field that the
   * underlying interval table is UNREVIEWED (`surveillanceIntervals.ts`'s own header) and must not
   * be presented to a clinician as validated guidance. */
  unreviewed: z.literal(true),
});

/** The billing-modifier recommendation for both payer categories, computed at read time from this
 * report's `indication` and whether any polyp was found — see `billingIntent.ts`'s header for why
 * Medicare and commercial diverge (PT vs 33) and why this is never stored. */
export const billingModifierResponseSchema = z.object({
  payerCategory: z.enum(PAYER_CATEGORIES),
  modifier: z.enum(['PT', '33']).nullable(),
  reason: z.string(),
  unreviewed: z.literal(true),
});

export const endoscopyReportResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  procedureType: z.string(),
  indication: z.string(),
  performedOn: calendarDateSchema,
  performingProviderId: z.uuid(),
  consentDocumentId: z.uuid(),
  insertionTimeMinutes: z.number().nullable(),
  withdrawalTimeMinutes: z.number().nullable(),
  cecalIntubationReached: z.boolean().nullable(),
  bowelPrepScore: bostonBowelPrepScoreResponseSchema.nullable(),
  bowelPrepTotal: z.number().int().nullable(),
  /** UNREVIEWED — see `bowelPrep.ts`'s header. `null` when no bowel prep score was recorded. */
  bowelPrepAdequate: z.boolean().nullable(),
  /** UNREVIEWED — see `bowelPrep.ts`'s header. `null` when no withdrawal time was recorded. */
  withdrawalTimeAdequate: z.boolean().nullable(),
  piecemealResectionOfLargePolyp: z.boolean(),
  confidentialityReasons: z.array(z.string()),
  polyps: z.array(polypResponseSchema),
  surveillanceRecommendation: surveillanceRecommendationResponseSchema,
  billingModifiers: z.array(billingModifierResponseSchema),
});
export type EndoscopyReportResponse = z.infer<typeof endoscopyReportResponseSchema>;

export const endoscopyReportEnvelopeSchema = z.object({ report: endoscopyReportResponseSchema });
export const endoscopyReportListEnvelopeSchema = z.object({
  reports: z.array(endoscopyReportResponseSchema),
});

// --- Adenoma Detection Rate (org-wide, provider-scoped) -------------------------------------------

export const adrQuerySchema = z.object({
  providerId: z.uuid(),
  from: calendarDateSchema,
  to: calendarDateSchema,
});
export type AdrQuery = z.infer<typeof adrQuerySchema>;

export const adrResponseSchema = z.object({
  providerId: z.uuid(),
  from: calendarDateSchema,
  to: calendarDateSchema,
  screeningExamCount: z.number().int().nonnegative(),
  adenomaPositiveScreeningExamCount: z.number().int().nonnegative(),
  rate: z.number().nullable(),
});
export type AdrResponse = z.infer<typeof adrResponseSchema>;

export const adrEnvelopeSchema = z.object({ adr: adrResponseSchema });

// --- Failure vocabulary -----------------------------------------------------------------------

/** Closed union, extending `encounterRequests.ts`'s `CLINICAL_ERROR_CODES` the same way
 * `dermatologyRequests.ts`'s `DERMATOLOGY_ERROR_CODES` does. `not_entitled` mirrors that module's
 * reasoning exactly; `consent_not_on_file` mirrors `aestheticsRequests.ts`'s own code for the
 * identical shape of refusal (a write refused for lacking the required consent on file). */
export const GASTROENTEROLOGY_ERROR_CODES = [
  ...CLINICAL_ERROR_CODES,
  'not_entitled',
  'consent_not_on_file',
] as const;
export type GastroenterologyErrorCode = (typeof GASTROENTEROLOGY_ERROR_CODES)[number];
